import * as Yup from 'yup';
import AllForm from '../../Stateless/Form';
import { Box, Button, TextareaAutosize, Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { COLORS } from '../../../theme/themeConstants';
import { FieldProps, FormikProps } from 'formik';
import { useGetAllCustomer } from '../../Query/CustomerQuery';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useAllDriverManagement } from '../../Query/DriverManagerQuery';
import { useAllMerchantforPushNotification } from '../../Query/MerchantQuery';
import { Autocomplete, TextField } from '@mui/material';
import { UseCreatePushNotification } from '../../Query/PushNotificationQuery';
import ImageUploadField from '../../Stateless/ImageUploadField';
import { usePermissions } from '../../../context/PermissionContext';




const validationSchema = Yup.object().shape({
    sendTo: Yup.string().required('Please select an option'),
    userId: Yup.string()
        .when('sendTo', {
            is: 'Particular User',
            then: (schema) => schema.required('Please select a customer'),
            otherwise: (schema) => schema.notRequired(),
        }),
    deliveryBoyId: Yup.string()
        .when('sendTo', {
            is: 'Particular Delivery Boy',
            then: (schema) => schema.required('Please select a Delivery Boy'),
            otherwise: (schema) => schema.notRequired(),
        }),
    merchantId: Yup.string()
        .when('sendTo', {
            is: 'Particular Merchant',
            then: (schema) => schema.required('Please select a Merchant'),
            otherwise: (schema) => schema.notRequired(),
        }),
    // restaurantId: Yup.string().required('Please select a restaurant'),
    // title: Yup.string().required('Please enter Title of Push Notification'),
    title: Yup.string()
        .required('Please enter Title of Push Notification')
        .max(50, 'Title cannot exceed 50 characters')
        .matches(
          /^[^0-9]+$/,
          'Title cannot contain numbers'
        )
        .test(
          'no-leading-trailing-spaces',
          'Title cannot start or end with spaces',
          (value) => value?.trim() === value
        )
        .test(
          'not-only-spaces',
          'Title cannot be only spaces',
          (value) => value?.trim().length > 0
        )
        .test(
          'not-start-with-special',
          'Title cannot start with a special character',
          (value) => {
            if (!value) return true;
            const trimmed = value.trim();
            if (trimmed.length === 0) return true;
            // Check if first character is a special character
            return !/^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(trimmed);
          }
        )
        .test(
          'not-only-special-chars',
          'Title cannot consist only of special characters',
          (value) => {
            if (!value) return true;
            const trimmed = value.trim();
            if (trimmed.length === 0) return true;
            // Check if the string contains at least one letter or space (not just special chars)
            return /[a-zA-Z\s]/.test(trimmed);
          }
        ),
    message: Yup.string().required('Please enter message'),
    // image: Yup.string().required('Please select image'),
});


const PushNotification = ({ onCancel }: { onCancel?: () => void }) => {
    const { hasPermission, isSubAdmin } = usePermissions();

    // Check if user has permission to create push notifications
    // pushNotification only has 'Add' action, so we check for that
    const canCreate = !isSubAdmin || hasPermission('pushNotification', 'Add');
    
    // If subadmin doesn't have permission, show message
    if (isSubAdmin && !canCreate) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="error">
                    You don't have permission to access Push Notifications
                </Typography>
            </Box>
        );
    }

     const { mutate: CreateChoice } =
        UseCreatePushNotification();
    // Fetch customer data
    const { data: customerData, isLoading: customerLoading } = useGetAllCustomer({
        page: 1, 
        limit: 100, 
        sortBy: "name",
        sortOrder: "desc"
    });

    // Fetch store data
    const { data: storeData, isLoading: storeLoading } = useAllStores({
        page: 1, 
        limit: 100, 
        sortBy: "createdAt",
        sortOrder: "desc",
        statusFilter: "All",
        addedByFilter: "All",
        storeType: "",
        merchantId: undefined,
        search: undefined
    });

    // Fetch delivery boy data
    const { data: deliveryBoyData, isLoading: deliveryBoyLoading } = useAllDriverManagement({
        page: 1, 
        limit: 100, 
        sortBy: "createdAt",
        sortOrder: "desc"
    });

    // Fetch merchant data
    const { data: merchantData, isLoading: merchantLoading } = useAllMerchantforPushNotification({
        page: 1, 
        limit: 100, 
        sortBy: "createdAt",
        sortOrder: "desc"
    });

    const customerOptions = customerData?.data?.map((item: any) => ({
        value: item.userId,
        label: item.name,
    })) || [];

    console.log("storeData", storeData);
    const storeOptions = storeData?.data?.map((item: any) => ({
        value: item.id,
        label: item.nameEnglish,
    })) || [];
    console.log("storeOptions", storeOptions);

    const deliveryBoyOptions = deliveryBoyData?.data?.map((item: any) => ({
        value: item.deliverBoyId,
        label: `${item.firstName} ${item.lastName}`,
    })) || [];

    console.log("merchantData", merchantData);
    console.log("merchantLoading", merchantLoading);
    
    const merchantOptions = merchantData?.data?.map((item: any) => ({
        value: item.id,
        label: `${item.firstName} ${item.lastName}`,
    })) || [];
    
    console.log("merchantOptions", merchantOptions);

    // Custom handler for sendPushNotificationTo radio button changes
    const handleSendPushNotificationToChange = (form: any, newValue: string) => {
        // Reset dependent fields when radio button changes
        form.setFieldValue('userId', '');
        form.setFieldValue('deliveryBoyId', '');
        form.setFieldValue('merchantId', '');
        form.setFieldValue('restaurantId', '');
        form.setFieldValue('title', '');
        form.setFieldValue('message', '');
        form.setFieldValue('image', null);
        
        // Clear touched state for dependent fields
        form.setFieldTouched('userId', false);
        form.setFieldTouched('deliveryBoyId', false);
        form.setFieldTouched('merchantId', false);
        form.setFieldTouched('restaurantId', false);
        form.setFieldTouched('title', false);
        form.setFieldTouched('message', false);
        form.setFieldTouched('image', false);
        
        // Clear errors for dependent fields
        form.setFieldError('userId', undefined);
        form.setFieldError('deliveryBoyId', undefined);
        form.setFieldError('merchantId', undefined);
        form.setFieldError('restaurantId', undefined);
        form.setFieldError('title', undefined);
        form.setFieldError('message', undefined);
        form.setFieldError('image', undefined);
        
        // Set the new value
        form.setFieldValue('sendTo', newValue);
        form.setFieldTouched('sendTo', true, false);
    };

    return (
        <Box sx={{
            backgroundColor: '#f5f5f5',
            minHeight: '100vh',
            padding: 3
        }}>
            <Typography
                variant="h5"
                sx={{
                    fontWeight: 600,
                    marginBottom: 3,
                    color: '#333',
                    textAlign: 'left'
                }}
            >
                Push Notification
            </Typography>
            <Box sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                padding: 4,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                maxWidth: 900,
                margin: '0 auto'
            }}>
                {AllForm({
                    initialValues: {
                        sendTo: 'All Users',
                        userId: '',
                        deliveryBoyId: '',
                        merchantId: '',
                        restaurantId: '',
                        title: '',
                        message: '',
                        image: '',
                    },
                    validationSchema,
                    validateOnChange: true,
                    enableReinitialize: true,
                    onSubmit: async (values: any) => {
                        try {
                            console.log('Push Notification values:', values);
                            
                            // Transform form values to API format
                            const apiPayload = {
                                sendTo: values.sendTo === 'All Users' ? 'ALL_CUSTOMERS' :
                                        values.sendTo === 'Particular User' ? 'PARTICULAR_CUSTOMER' :
                                        values.sendTo === 'All Delivery Boys' ? 'ALL_DELIVERY_BOYS' :
                                        values.sendTo === 'Particular Delivery Boy' ? 'PARTICULAR_DELIVERY_BOY' :
                                        values.sendTo === 'All Merchant' ? 'ALL_MERCHANTS' :
                                        values.sendTo === 'Particular Merchant' ? 'PARTICULAR_MERCHANT' : 'ALL_CUSTOMERS',
                                customerId: values.sendTo === 'Particular User' ? values.userId : undefined,
                                deliveryBoyId: values.sendTo === 'Particular Delivery Boy' ? values.deliveryBoyId : undefined,
                                merchantId: values.sendTo === 'Particular Merchant' ? values.merchantId : undefined,
                                storeId: values.restaurantId,
                                title: values.title,
                                type: 'IMAGE',
                                message: values.message || '',
                                ...(values.image ? { imageUrl: values.image } : {})
                            };
                            
                            console.log('API Payload:', apiPayload);
                            CreateChoice({ modifiedValues: apiPayload });
                        } catch (error) {
                            console.error('Error submitting push notification:', error);
                        }
                    },
                    fields: [
                        {
                            name: 'sendTo',
                            label: 'Send Push Notification To:',
                            type: 'customWithError',
                            isRequired: true,
                            component: ({ field, form }: any) => {
                                const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
                                
                                return (
                                    <Box sx={{ width: 400 }}>
                                        <RadioGroup
                                            row
                                            name={field.name}
                                            value={form.values[field.name]}
                                            onChange={(e) => {
                                                handleSendPushNotificationToChange(form, e.target.value);
                                            }}
                                            sx={{ 
                                                gap: 2.5, 
                                                mt: 0.5,
                                            }}
                                        >
                                            {['All Users', 'Particular User', 'All Delivery Boys', 'Particular Delivery Boy', 'All Merchant', 'Particular Merchant'].map((option) => (
                                                <FormControlLabel
                                                    key={option}
                                                    value={option}
                                                    control={
                                                        <Radio
                                                            size="small"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                '&.Mui-checked': {
                                                                    color: 'primary.main',
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography
                                                            sx={{
                                                                fontSize: 14,
                                                                color: COLORS.text.secondary,
                                                            }}
                                                        >
                                                            {option}
                                                        </Typography>
                                                    }
                                                    sx={{
                                                        margin: 0,
                                                        '& .MuiFormControlLabel-label': {
                                                            fontSize: 14,
                                                            color: 'text.secondary',
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </RadioGroup>
                                        {hasError && (
                                            <Box sx={{ mt: 1, fontSize: 12, color: '#d32f2f' }}>
                                                {form.errors[field.name]}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            },
                        },
                        {
                            name: 'userId',
                            label: 'Customers:',
                            type: 'customWithError',
                            dependsOn: { field: 'sendTo', value: 'Particular User' },
                            isRequired: true,
                            component: ({ field, form }: any) => {
                                const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);

                                return (
                                    <Box sx={{ width: 400 }}>
                                        <Autocomplete
                                            options={customerOptions}
                                            loading={customerLoading}
                                            getOptionLabel={(option) => option.label}
                                            value={
                                                form.values[field.name]
                                                    ? customerOptions.find((opt :any) => opt.value === form.values[field.name])
                                                    : null
                                            }
                                            onChange={(_, newValue) => {
                                                form.setFieldValue(field.name, newValue ? newValue.value : '');
                                                form.setFieldTouched(field.name, true, false);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={hasError}
                                                    placeholder="-- Select --"
                                                    sx={{
                                                        mb: 0,
                                                        '& .MuiInputBase-root': {
                                                            fontSize: 14,
                                                            color: 'text.primary',
                                                            width: '400px',
                                                            height: '40px',
                                                        },
                                                        '& .MuiFormLabel-root': {
                                                            fontSize: 14,
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                );
                            },
                        },
                        {
                            name: 'deliveryBoyId',
                            label: 'Delivery Boy:',
                            type: 'customWithError',
                            dependsOn: { field: 'sendTo', value: 'Particular Delivery Boy' },
                            isRequired: true,
                            component: ({ field, form }: any) => {
                                const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);

                                return (
                                    <Box sx={{ width: 400 }}>
                                        <Autocomplete
                                            options={deliveryBoyOptions}
                                            loading={deliveryBoyLoading}
                                            getOptionLabel={(option) => option.label}
                                            value={
                                                form.values[field.name]
                                                    ? deliveryBoyOptions.find((opt : any) => opt.value === form.values[field.name])
                                                    : null
                                            }
                                            onChange={(_, newValue) => {
                                                form.setFieldValue(field.name, newValue ? newValue.value : '');
                                                form.setFieldTouched(field.name, true, false);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={hasError}
                                                    placeholder="-- Select --"
                                                    sx={{
                                                        mb: 0,
                                                        '& .MuiInputBase-root': {
                                                            fontSize: 14,
                                                            color: 'text.primary',
                                                            width: '400px',
                                                            height: '40px',
                                                        },
                                                        '& .MuiFormLabel-root': {
                                                            fontSize: 14,
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                );
                            },
                        },
                        {
                            name: 'merchantId',
                            label: 'Merchant:',
                            type: 'customWithError',
                            dependsOn: { field: 'sendTo', value: 'Particular Merchant' },
                            isRequired: true,
                            component: ({ field, form }: any) => {
                                const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);

                                return (
                                    <Box sx={{ width: 400 }}>
                                        <Autocomplete
                                            options={merchantOptions}
                                            loading={merchantLoading}
                                            getOptionLabel={(option) => option.label}
                                            value={
                                                form.values[field.name]
                                                    ? merchantOptions.find((opt: any) => opt.value === form.values[field.name])
                                                    : null
                                            }
                                            onChange={(_, newValue) => {
                                                form.setFieldValue(field.name, newValue ? newValue.value : '');
                                                form.setFieldTouched(field.name, true, false);
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={hasError}
                                                    placeholder="-- Select --"
                                                    sx={{
                                                        mb: 0,
                                                        '& .MuiInputBase-root': {
                                                            fontSize: 14,
                                                            color: 'text.primary',
                                                            width: '400px',
                                                            height: '40px',
                                                        },
                                                        '& .MuiFormLabel-root': {
                                                            fontSize: 14,
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                );
                            },
                        },
                        {
                            name: 'restaurantId',
                            label: '',
                            type: 'customWithError',
                            // dependsOn: { field: 'sendPushNotificationTo', value: 'All Delivery Boys' },
                            // isRequired: true,
                            component: ({ field, form }: any) => {
                                const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
                                // Hide Store selection for delivery boy targeting
                                const shouldShow = !['All Delivery Boys', 'Particular Delivery Boy','All Merchant', 'Particular Merchant' ].includes(form.values.sendTo);

                                if (!shouldShow) {
                                    return <></>;
                                }

                                return (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography sx={{ width: 90, fontSize: 14, color: 'text.primary' }}>Select Store:</Typography>
                                            <Box sx={{ width: 400 }}>
                                                <Autocomplete
                                                    options={storeOptions}
                                                    loading={storeLoading}
                                                    getOptionLabel={(option) => option.label}
                                                    value={
                                                        form.values[field.name]
                                                            ? storeOptions.find((opt) => opt.value === form.values[field.name])
                                                            : null
                                                    }
                                                    onChange={(_, newValue) => {
                                                        form.setFieldValue(field.name, newValue ? newValue.value : '');
                                                        form.setFieldTouched(field.name, true, false);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            error={hasError}
                                                            placeholder="-- Select --"
                                                            sx={{
                                                                mb: 0,
                                                                '& .MuiInputBase-root': {
                                                                    fontSize: 14,
                                                                    color: 'text.primary',
                                                                    width: '400px',
                                                                    height: '40px',
                                                                },
                                                                '& .MuiFormLabel-root': {
                                                                    fontSize: 14,
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Box>
                                        {hasError && (
                                            <Box sx={{ mt: 1, ml: '120px', fontSize: 12, color: '#d32f2f' }}>
                                                {form.errors[field.name]}
                                            </Box>
                                        )}
                                    </>
                                );
                            },
                        },
                        {
                            name: 'title',
                            label: 'Title:',
                            type: 'text',
                            placeholder: 'Title',
                            isRequired: true,
                        },
                        {
                            name: 'message',
                            label: 'Message:',
                            type: 'customWithError',
                            isRequired: true,
                            component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
                                const error = form.touched.message && Boolean(form.errors.message);
                                return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: 400 }}>
                                        <TextareaAutosize
                                            id={field.name}
                                            minRows={4}
                                            maxRows={10}
                                            placeholder="Message"
                                            style={{
                                                padding: 8,
                                                fontSize: 14,
                                                borderRadius: 4,
                                                border: `1px solid ${error ? '#d32f2f' : '#ccc'}`,
                                                outline: 'none',
                                                resize: 'vertical',
                                            }}
                                            {...field}
                                            onBlur={form.handleBlur}
                                            onChange={form.handleChange}
                                        />
                                    </Box>
                                );
                            },
                        },
                        {
                            name: 'image',
                            label: 'Image:',
                            type: 'custom',
                            component: ({ field, form }: any) => {
                                const hasError = form.touched[field.name] && Boolean(form.errors[field.name]);
                                return (
                                    <Box sx={{ width: 400 }}>
                                        <ImageUploadField
                                            name="image"
                                            label="Push Notification Image"
                                        />
                                        {hasError && (
                                            <Box sx={{ mt: 1, fontSize: 12, color: '#d32f2f' }}>
                                                {form.errors[field.name]}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            },
                        },
                    ],
                    customButton: (formikProps: any) => (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {canCreate && (
                                <Button
                                    variant="contained"
                                    onClick={formikProps.handleSubmit}
                                    sx={{
                                        backgroundColor: COLORS.primary.main,
                                        textTransform: 'none',
                                        '&:hover': { backgroundColor: COLORS.primary.dark },
                                    }}
                                >
                                    Submit
                                </Button>
                            )}
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={formikProps.resetForm}
                                sx={{
                                    textTransform: 'none',
                                    color: COLORS.secondary.main,
                                    borderColor: COLORS.secondary.main,
                                    '&:hover': {
                                        color: COLORS.secondary.main,
                                        borderColor: COLORS.secondary.main,
                                    },
                                }}
                            >
                                Reset
                            </Button>
                            {onCancel && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={onCancel}
                                    sx={{
                                        bgcolor: '#f44336',
                                        color: '#fff',
                                        '&:hover': {
                                            bgcolor: '#d32f2f',
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                            )}
                        </Box>
                    ),
                })()}
            </Box>
        </Box>
    );
};

export default PushNotification;

